import React, { useState, useEffect, useRef } from "react";
import { Container, Card, Button, ProgressBar } from 'react-bootstrap';
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMap, faRulerCombined, faSeedling, faWater, faChevronRight, faChevronLeft, faHome } from '@fortawesome/free-solid-svg-icons';
import PageTitle from '../common/PageTitle';
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import { getUserId, cleanOtherUsersData } from '../../utils/auth';
import './WizardWrappers.css';

const WizardWrapper = ({ children, currentStep }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Ajout de la référence pour accéder aux composants enfants
  const childRef = useRef(null);
  
  // Initialiser correctement le wizard à chaque nouvelle session
  const [wizardData, setWizardData] = useState(() => {
    // Vérifier si une réinitialisation est demandée
    const resetAllForms = localStorage.getItem('resetAllForms');
    
    if (resetAllForms) {
      // Si oui, retourner un état vide sans charger depuis localStorage
      return {
        step0: { isCompleted: false, data: {} },
        step1: { isCompleted: false, data: {} },
        step2: { isCompleted: false, data: {} },
        step3: { isCompleted: false, data: {} },
        step4: { isCompleted: false, data: {} },
        fieldId: "",
        fieldName: "",
        zoneId: "",
        zoneName: "",
        cropTypeId: "",
        cropTypeName: ""
      };
    }
    
    // Récupérer l'ID utilisateur actuel
    const userId = getUserId();

    // Nettoyer les données des autres utilisateurs
    cleanOtherUsersData();
    
    // Charger les données spécifiques à l'utilisateur actuel
    const savedData = localStorage.getItem(`smartFarmWizardData_${userId}`);
    if (savedData) {
      try {
        return JSON.parse(savedData);
      } catch (e) {
        console.error("Error parsing saved wizard data:", e);
        localStorage.removeItem(`smartFarmWizardData_${userId}`);
      }
    }
    
    // État initial par défaut
    return {
      step0: { isCompleted: false, data: {} }, // Ajout de l'étape 0 pour Farm
      step1: { isCompleted: false, data: {} },
      step2: { isCompleted: false, data: {} },
      step3: { isCompleted: false, data: {} },
      step4: { isCompleted: false, data: {} },
      fieldId: "",
      fieldName: "",
      zoneId: "",
      zoneName: "",
      cropTypeId: "",
      cropTypeName: ""
    };
  });

  // Sauvegarder les données dans localStorage à chaque changement
  useEffect(() => {
    const userId = getUserId();
    localStorage.setItem(`smartFarmWizardData_${userId}`, JSON.stringify(wizardData));
  }, [wizardData]);

  // Vérifier si une réinitialisation est demandée lors du montage du composant
  useEffect(() => {
    const resetAllForms = localStorage.getItem('resetAllForms');
    
    if (resetAllForms) {
      // Réinitialiser les données du wizard
      resetAllWizardData();
      
      // Supprimer le flag global
      localStorage.removeItem('resetAllForms');
    }
  }, []);

  // Gérer la navigation entre les étapes
  const handleNext = () => {
    const nextStep = currentStep + 1;
    if (nextStep <= 4) {
      navigateToStep(nextStep);
    }
  };

 const handlePrevious = () => {
  // Demander aux enfants de sauvegarder leur état actuel via une référence
  if (childRef.current && childRef.current.saveState) {
    childRef.current.saveState();
  }
  
  const prevStep = currentStep - 1;
  if (prevStep >= 0) {
    navigateToStep(prevStep);
  }
};

  const navigateToStep = (step) => {
    switch (step) {
      case 0:
        navigate('/add-farm');
        break;
      case 1:
        navigate('/add-field');
        break;
      case 2:
        navigate('/add-soil-info');
        break;
      case 3:
        navigate('/add-crop-info');
        break;
      case 4:
        navigate('/add-irrigation');
        break;
      default:
        navigate('/add-field');
    }
  };

  // Ajouter cette fonction
  const resetAllWizardData = () => {
    const userId = getUserId();
    
    // Réinitialiser complètement l'état local
    setWizardData({
      step0: { isCompleted: false, data: {} },
      step1: { isCompleted: false, data: {} },
      step2: { isCompleted: false, data: {} },
      step3: { isCompleted: false, data: {} },
      step4: { isCompleted: false, data: {} },
      fieldId: "",
      fieldName: "",
      zoneId: "",
      zoneName: "",
      cropTypeId: "",
      cropTypeName: ""
    });
    
    // Supprimer du localStorage
    localStorage.removeItem(`smartFarmWizardData_${userId}`);
    
    // Définir le flag de réinitialisation
    localStorage.setItem('resetAllForms', 'true');
  };

  // Modifier la fonction completeStep pour garantir une mise à jour correcte
  const completeStep = (stepNumber, data) => {
    // Extraction des identifiants partagés
// Extraction des identifiants partagés
const sharedData = {
  ...(data.fieldId && { fieldId: data.fieldId }),
  ...(data.fieldName && { fieldName: data.fieldName }),
  ...(data.zoneId && { zoneId: data.zoneId }),
  ...(data.zoneName && { zoneName: data.zoneName }),
  ...(data.cropTypeId && { cropTypeId: data.cropTypeId }),
  ...(data.cropTypeName && { cropTypeName: data.cropTypeName }),
  ...(data.farmId && { farmId: data.farmId }),
  ...(data.farmName && { farmName: data.farmName })
};

// Si c'est l'étape Farm qui est complétée, assurons-nous que les données sont bien stockées
if (stepNumber === 0) {
  localStorage.setItem('lastAddedFarmId', data.farmId || "");
  localStorage.setItem('lastAddedFarmName', data.farmName || "");
}
    // Création d'un nouvel objet pour garantir une mise à jour complète
    const updatedWizardData = {
      ...wizardData,
      [`step${stepNumber}`]: { isCompleted: true, data },
      ...sharedData
    };
    
    // Mise à jour de l'état et du localStorage avec la clé spécifique à l'utilisateur
    setWizardData(updatedWizardData);
    
    // Utiliser la clé spécifique à l'utilisateur
    const userId = getUserId();
    localStorage.setItem(`smartFarmWizardData_${userId}`, JSON.stringify(updatedWizardData));
  };

  // Obtenir l'icône correspondant à l'étape
  const getStepIcon = (stepNumber) => {
    switch (stepNumber) {
      case 0: return faHome;        // Icône pour Farm
      case 1: return faMap;         // Icône pour Field
      case 2: return faRulerCombined; // Icône pour Soil
      case 3: return faSeedling;    // Icône pour Crop
      case 4: return faWater;       // Icône pour Irrigation
      default: return faMap;
    }
  };

  // Fonction pour calculer le pourcentage de progression
const calculateProgress = () => {
  // Si nous commençons par Farm (step 0), nous avons 5 étapes au total
  // Si nous commençons par Field (step 1), nous avons 4 étapes au total
  const isStartingWithFarm = currentStep === 0 || wizardData?.step0?.isCompleted;
  const totalSteps = isStartingWithFarm ? 5 : 4;
  const stepNumber = isStartingWithFarm ? currentStep + 1 : currentStep;
  return (stepNumber / totalSteps) * 100;
};

  // Vérifier si l'étape précédente est complétée (sauf pour l'étape 0 et 1)
  const isPreviousStepCompleted = () => {
    if (currentStep <= 1) return true;
    return wizardData[`step${currentStep - 1}`].isCompleted;
  };

  // Afficher un avertissement si l'étape précédente n'est pas complétée
  useEffect(() => {
    if (currentStep > 1 && !isPreviousStepCompleted()) {
      swal({
        title: t("Attention"),
        text: t("Step_warning", { currentStep }),
        icon: "warning",
        buttons: { 
          previous: t("Go_to_previous_step"),
          continue: t("Continuer_ici")
        }
      }).then(value => {
        if (value === "previous") {
          handlePrevious();
        }
      });
    }
  }, [currentStep]);

  return (
    <Container fluid className="wizard-page py-4 px-md-5">
      <style>
        {`
          .wizard-container {
            max-width: 1200px;
            margin: 0 auto;
          }
          .progress-tracker {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2rem;
            position: relative;
          }
          .progress-step {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 25%;
            z-index: 2;
          }
          .step-icon {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            background-color: #e9ecef;
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 8px;
            color: #6c757d;
            transition: all 0.3s;
          }
          .step-text {
            font-size: 14px;
            color: #161c21ff;
            text-align: center;
            transition: all 0.3s;
          }
          .active-step .step-icon {
            background-color: #26a6b7;
            color: white;
            box-shadow: 0 0 10px rgba(78, 115, 223, 0.5);
          }
          .active-step .step-text {
            color: #1d8290;
            font-weight: 600;
          }
          .completed-step .step-icon {
            background-color: #137a56;
            color: white;
          }
          .completed-step .step-text {
            color: #137a56;
          }
          .progress-bar-container {
            position: absolute;
            top: 25px;
            left: 0;
            width: 100%;
            height: 4px;
            background-color: #e9ecef;
            z-index: 1;
          }
          .form-card {
            border-radius: 10px;
            box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
            overflow: hidden;
          }
          .form-header {
            background: #1d8290;
            color: white;
            padding: 20px;
            font-weight: 500;
          }
          .form-body {
            padding: 30px;
            background: white;
          }
          .info-box {
            background: #f8f9fc;
            border-left: 4px solid #26a6b7;
            padding: 15px;
            margin-bottom: 20px;
            border-radius: 5px;
          }
          .btn-nav {
            margin-top: 1rem;
            padding: 0.5rem 1.5rem;
          }
        `}
      </style>

      <div className="wizard-container">
        <PageTitle subtitle={`${t('field_setup')}`} className="mb-3 text-center" />

        <div className="progress-tracker wizard-progress-tracker mb-4">
          <div className="progress-bar-container">
            <ProgressBar now={calculateProgress()} style={{ height: '4px' }} />
          </div>
          
          {/* Afficher uniquement l'étape actuelle et les étapes terminées */}
          {[0, 1, 2, 3, 4].map(stepNum => {
  // Déterminer si nous commençons par Farm ou Field
  const isStartingWithFarm = currentStep === 0 || wizardData?.step0?.isCompleted;
  
  // Si nous ne commençons pas par Farm et que c'est l'étape 0, ne pas l'afficher
  if (stepNum === 0 && !isStartingWithFarm) return null;
  
  // Calculer quelles étapes doivent être visibles
  let shouldShow = false;
  
  if (isStartingWithFarm) {
    // Si nous commençons par Farm, afficher toutes les étapes jusqu'à l'étape actuelle
    shouldShow = stepNum <= currentStep;
  } else {
    // Si nous commençons par Field, afficher les étapes 1-4 jusqu'à l'étape actuelle
    shouldShow = stepNum >= 1 && stepNum <= currentStep;
  }
  
  if (!shouldShow) return null;
  
  return (
              <div 
                key={stepNum} 
                className={`progress-step ${currentStep === stepNum ? 'active-step' : ''} ${wizardData[`step${stepNum}`]?.isCompleted ? 'completed-step' : ''}`}
                style={{ 
                  cursor: 'pointer',
                  // Calculer la largeur en fonction du nombre d'étapes visibles actuel
                  width: `${100 / (currentStep + 1)}%` 
                }}
                onClick={() => {
                  // Permettre la navigation uniquement vers les étapes complétées ou l'étape actuelle
                  if (stepNum <= currentStep) {
                    navigateToStep(stepNum);
                  }
                }}
              >
                <div className="step-icon">
                  <FontAwesomeIcon icon={getStepIcon(stepNum)} />
                </div>
                <div className="step-text">
                  {stepNum === 0 && t('farm_setup')}
                  {stepNum === 1 && t('Field_Info')}
                  {stepNum === 2 && t('soil_info')}
                  {stepNum === 3 && t('crop_info')}
                  {stepNum === 4 && t('irrigation_info')}
                </div>
              </div>
            );
          })}
        </div>

        <Card className="form-card wizard-form-card">
         <Card.Header
  className="wizard-form-header"
  style={{
    background: "#1d8290",
    color: "white",
    borderRadius: "12px 12px 0 0",
    padding: "15px 20px",
    fontSize: "18px",
    fontWeight: "600",
  }}
>
  <div className="d-flex justify-content-between align-items-center">
    <div>
      <FontAwesomeIcon icon={getStepIcon(currentStep)} className="me-2" />
      <span style={{ color: "#fff", fontWeight: "bold" }}>
        {currentStep === 0 && t("step_0_title")}
        {currentStep === 1 && t("step_1_title")}
        {currentStep === 2 && t("step_2_title")}
        {currentStep === 3 && t("step_3_title")}
        {currentStep === 4 && t("step_4_title")}
      </span>
    </div>
    <div
  className="small"
  style={{
    color: "#fff",         // blanc foncé
    fontWeight: "bold",    // gras
    fontSize: "16px",      // optionnel, pour le rendre plus visible
    letterSpacing: "0.5px" // optionnel, pour la lisibilité
  }}
>
  {t("step")}  {currentStep === 0 || wizardData?.step0?.isCompleted ? currentStep + 1 : currentStep} {t("of")}  {currentStep === 0 || wizardData?.step0?.isCompleted ? 5 : 4}
</div>
  </div>
</Card.Header>
          
          <Card.Body className="form-body p-0">
    <div className="info-box wizard-info-box m-4">
      <p className="mb-0">
        {currentStep === 0 && t("step_0_info")}
        {currentStep === 1 && t("step_1_info")}
        {currentStep === 2 && t("step_2_info")}
        {currentStep === 3 && t("step_3_info")}
        {currentStep === 4 && t("step_4_info")}
      </p>
    </div>

            {/* Injecter le contenu de l'étape actuelle avec la référence */}
            {React.cloneElement(children, {
              wizardData: wizardData,
              completeStep: completeStep,
              onPrevious: handlePrevious,
              onNext: handleNext,
              resetAllData: resetAllWizardData,
              isWizardMode: true,
              ref: childRef // Ajout de la référence aux composants enfants
            })}
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default WizardWrapper;
